/* How late is late enough to be a conflict.
 *
 * A technician who will be ready at 2:03 for a 2:00 client is not a problem the
 * desk needs telling about: the client is still hanging their coat up. Flagged
 * anyway, the conflicts list filled up with bookings that were going to be fine,
 * and a list that is mostly false alarms is a list the desk stops reading.
 *
 * So there is a buffer — the salon's, in `tt_settings` as
 * `conflictBufferMinutes` — and a booking only counts as coming up without its
 * technician when they will be ready MORE than that long after it starts. Five
 * minutes out of the box. Zero puts the strict reading back, where a minute
 * over is a minute over.
 *
 * It applies to the two things that make somebody late for a client: a square
 * still running, and another booking of theirs that runs into this one. For the
 * second the question is asked of whichever of the pair starts later, because
 * that is the client left waiting — the technician is ready for them when the
 * earlier booking ends. Two bookings at the same minute are never inside the
 * buffer: one of those clients waits the whole of the other's service.
 *
 * It does not apply to the end of somebody's day. Going home at five with a
 * client booked until half past is not a few minutes' wait for anybody, and the
 * shift check keeps its own rules (js/rules/shift.js).
 *
 * Pure, like the rest of js/rules: times are epoch milliseconds and the buffer
 * arrives already in milliseconds. This file imports nothing.
 */

export var BUFFER_DEFAULT_MIN = 5;
export var BUFFER_CHOICES = [0, 5, 10];
export var BUFFER_LIMITS = { min: 0, max: 60 };

/* A stored buffer, in minutes, as the board will use it. Anything unreadable is
 * the default rather than zero: zero is a real choice a salon can make, and an
 * unreadable row should not make it for them. */
export function cleanBuffer(value) {
  if (value === '' || value == null) return BUFFER_DEFAULT_MIN;
  var n = Number(value);
  if (!isFinite(n)) return BUFFER_DEFAULT_MIN;
  return Math.min(BUFFER_LIMITS.max, Math.max(BUFFER_LIMITS.min, Math.round(n)));
}

/* Will somebody who is ready at `readyAt` be late for a client due at
 * `startsAt`? A ready time of null is a square with no expected end, which
 * says nothing about when they are out of it — so it is late. */
export function readyTooLate(readyAt, startsAt, bufferMs) {
  if (readyAt == null) return true;
  return readyAt > startsAt + (bufferMs || 0);
}

/* Do two bookings on one technician collide, given the buffer? `a` and `b` are
 * `{ from, to }`. Whoever starts second waits for the other to end; it is a
 * collision when that wait is more than the buffer. With no buffer this is
 * exactly an overlap test. */
export function bookingsCollide(a, b, bufferMs) {
  if (!(a.from < b.to && b.from < a.to)) return false;
  var first = a.from <= b.from ? a : b;
  var second = first === a ? b : a;
  if (first.from === second.from) return true;
  return readyTooLate(first.to, second.from, bufferMs);
}
